import React, { useEffect, useState } from 'react';
import { NodeProps } from '@xyflow/react';
import { BaseNode, createInputHandle, createOutputHandle } from './BaseNode';
import { useWorkflowStore } from '@/lib/workflows/store';
import { NodeExecutionStatus } from 'types';

import useLocale from 'next-translate/useTranslation';
import Button from '@/components/buttons/Button';
import { PlayIcon, RefreshIcon } from '@heroicons/react/outline';
import clsxm from '@/lib/clsxm';

import { SimpleTooltip } from '@/components/ui/tooltip';

// Helper function to download image
const downloadImage = async (url: string, filename: string) => {
  try {
    const response = await fetch(url);
    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(blobUrl);
  } catch (error) {
    console.error('Download failed:', error);
  }
};

interface ImageSplitterConfig {
  rows: number;
  columns: number;
}

interface ImageSplitterNodeProps extends NodeProps {
  onRunNode?: (nodeId: string) => void;
  nodeId?: string;
  readOnly?: boolean;
}

export function ImageSplitterNode({
  id,
  data,
  selected,
  onRunNode,
  nodeId,
  readOnly,
}: ImageSplitterNodeProps) {
  const { t } = useLocale('common');
  const updateNodeData = useWorkflowStore((state) => state.updateNodeData);
  const addNode = useWorkflowStore((state) => state.addNode);
  const allNodes = useWorkflowStore((state) => state.nodes);

  // State to control settings visibility
  const [showSettings, setShowSettings] = useState(!data.result);
  const [isLandscape, setIsLandscape] = useState(false);

  const executionStatus =
    (data.status as NodeExecutionStatus | undefined) || 'idle';
  const isRunning = executionStatus === 'running';

  // Auto-collapse settings when result is generated
  useEffect(() => {
    if (data.result && showSettings) {
      setShowSettings(false);
    }
  }, [data.result]);

  // Hide result if running
  const shouldShowResult = !!data.result && !isRunning;

  const config: ImageSplitterConfig = {
    rows: (data.config as ImageSplitterConfig)?.rows || 2,
    columns: (data.config as ImageSplitterConfig)?.columns || 2,
  };

  const totalOutputs = config.rows * config.columns;

  const handleRowsChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    updateNodeData(id, {
      config: {
        ...config,
        rows: Number(e.target.value),
      },
    });
  };

  const handleColumnsChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    updateNodeData(id, {
      config: {
        ...config,
        columns: Number(e.target.value),
      },
    });
  };

  // When multiple images are generated, create separate select-image nodes
  // so each image can be used independently in the workflow.
  useEffect(() => {
    if (!Array.isArray(data.result) || data.result.length <= 1) {
      return;
    }

    const imageUrls = (data.result as unknown[]).filter(
      (url): url is string => typeof url === 'string',
    );

    if (imageUrls.length <= 1) return;

    const parentNode = allNodes.find((n) => n.id === id);
    if (!parentNode) return;

    const existingChildren = allNodes.filter((n) => {
      const nodeData = n.data as any;
      return (
        n.type === 'select-image' &&
        nodeData?.sourceNodeId === id &&
        typeof nodeData?.resultIndex === 'number'
      );
    });

    const childrenByIndex = new Map<
      number,
      (typeof existingChildren)[number]
    >();
    existingChildren.forEach((node) => {
      const idx = (node.data as any).resultIndex;
      if (typeof idx === 'number') {
        childrenByIndex.set(idx, node);
      }
    });

    imageUrls.forEach((url, index) => {
      // Keep the original node as-is; create/select nodes for additional images
      if (index === 0) return;

      const existingChild = childrenByIndex.get(index);
      if (existingChild) {
        const existingConfig = ((existingChild.data as any).config || {}) as {
          selectedImageUrl?: string;
          selectedIsVideo?: boolean;
        };

        // Skip update if already in sync to avoid infinite update loops
        if (
          existingConfig.selectedImageUrl === url &&
          existingConfig.selectedIsVideo === false
        ) {
          return;
        }

        updateNodeData(existingChild.id, {
          config: {
            ...(existingChild.data as any).config,
            selectedImageUrl: url,
            selectedIsVideo: false,
          },
          result: url,
        });
        return;
      }

      const offsetX = 320 * index;

      const newNode: any = {
        id: `select-image-${Date.now()}-${index}-${id}`,
        type: 'select-image',
        position: {
          x: parentNode.position.x + offsetX,
          y: parentNode.position.y,
        },
        data: {
          label: `Split image ${index + 1}`,
          type: 'select-image',
          config: {
            selectedImageUrl: url,
            selectedIsVideo: false,
          },
          result: url,
          sourceNodeId: id,
          resultIndex: index,
        },
      };

      addNode(newNode);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, data.result, allNodes]);

  return (
    <BaseNode
      title={(data.label as string) || t('Image Splitter')}
      icon={
        <svg
          className='w-4 h-4 text-purple-600'
          fill='none'
          strokeLinecap='round'
          strokeLinejoin='round'
          strokeWidth='2'
          viewBox='0 0 24 24'
          stroke='currentColor'
        >
          <path d='M4 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM14 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1v-4zM14 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z' />
        </svg>
      }
      hasInput={false}
      hasOutput={false}
      status={executionStatus}
      selected={selected}
      nodeId={nodeId || id}
      onRunNode={onRunNode}
      readOnly={readOnly}
      customInputs={createInputHandle('image', 'image', 50)}
      customOutputs={createOutputHandle('image', 'image', 50)}
      disableMinWidth={!showSettings && !!data.result}
      customHeaderButtons={
        (data as any)?.result ? (
          <SimpleTooltip
            title={showSettings ? t('Hide settings') : t('Show settings')}
            side='top'
          >
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowSettings(!showSettings);
              }}
              className='flex-shrink-0 p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors'
            >
              <svg
                className='w-5 h-5 text-gray-600 dark:text-gray-300'
                fill='none'
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth='2'
                viewBox='0 0 24 24'
                stroke='currentColor'
              >
                <path d='M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z' />
                <path d='M15 12a3 3 0 11-6 0 3 3 0 016 0z' />
              </svg>
            </button>
          </SimpleTooltip>
        ) : undefined
      }
    >
      <div className={showSettings || !data.result ? 'w-[400px]' : ''}>
        {/* Collapsible Settings Section */}
        <div
          className={`space-y-3 px-4 pt-3 pb-3 border-b border-gray-200 dark:border-gray-700 ${
            showSettings ? '' : 'hidden'
          }`}
        >
          {/* Rows Selection */}
          <div>
            <label className='block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1'>
              {t('Rows')}
            </label>
            <select
              value={config.rows}
              onChange={handleRowsChange}
              className='w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:border-white/5 dark:bg-slate-700/50 dark:text-white'
              disabled={readOnly}
            >
              {[1, 2, 3, 4, 5].map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </div>

          {/* Columns Selection */}
          <div>
            <label className='block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1'>
              {t('Columns')}
            </label>
            <select
              value={config.columns}
              onChange={handleColumnsChange}
              className='w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:border-white/5 dark:bg-slate-700/50 dark:text-white'
              disabled={readOnly}
            >
              {[1, 2, 3, 4, 5].map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </div>

          {/* Output Count Info */}
          <div className='text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800 px-2 py-1.5 rounded'>
            {t('Output')}: {totalOutputs}{' '}
            {totalOutputs > 1 ? t('images') : t('image')}
          </div>

          {/* Run Button (inside settings) */}
          <div className='pt-1'>
            <Button
              className='w-full justify-center'
              variant='outline'
              isLoading={isRunning}
              onClick={(e) => {
                e.stopPropagation();
                onRunNode?.(nodeId || id);
              }}
            >
              {data.result ? (
                <>
                  <RefreshIcon className='w-4 h-4 mr-2' />
                  <span>{t('Re-split')}</span>
                </>
              ) : (
                <>
                  <PlayIcon className='w-4 h-4 mr-2' />
                  <span>{t('Split')}</span>
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Result Preview - show only first image */}
        {shouldShowResult && (
          <div
            className={clsxm(
              showSettings
                ? 'w-full px-4 pb-3'
                : isLandscape
                  ? 'w-[700px]'
                  : 'w-[420px]',
            )}
          >
            {(() => {
              const singleUrl =
                typeof data.result === 'string'
                  ? (data.result as string)
                  : Array.isArray(data.result) && data.result.length > 0
                    ? (data.result[0] as string)
                    : null;

              if (!singleUrl) return null;

              return (
                <div className='relative group rounded-md overflow-hidden'>
                  <img
                    src={singleUrl}
                    alt='Split result'
                    className='w-full h-auto object-cover'
                    onLoad={(e) => {
                      const img = e.currentTarget;
                      setIsLandscape(img.naturalWidth > img.naturalHeight);
                    }}
                  />
                  <div className='absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity'>
                    <button
                      onClick={() => window.open(singleUrl, '_blank')}
                      className='p-1.5 bg-white rounded-md shadow-lg hover:bg-gray-50 transition-colors'
                      title={t('Open in new tab')}
                    >
                      <svg
                        className='w-4 h-4 text-gray-700'
                        fill='none'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth='2'
                        viewBox='0 0 24 24'
                        stroke='currentColor'
                      >
                        <path d='M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14' />
                      </svg>
                    </button>
                    <button
                      onClick={() =>
                        downloadImage(singleUrl, 'split-image.png')
                      }
                      className='p-1.5 bg-white rounded-md shadow-lg hover:bg-gray-50 transition-colors'
                      title={t('Download')}
                    >
                      <svg
                        className='w-4 h-4 text-gray-700'
                        fill='none'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth='2'
                        viewBox='0 0 24 24'
                        stroke='currentColor'
                      >
                        <path d='M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4' />
                      </svg>
                    </button>
                  </div>
                </div>
              );
            })()}
          </div>
        )}

        {/* Error Display */}
        {!!data.error && (
          <div className='mx-3 mb-3 border-t border-red-200 bg-red-50 px-2 py-2 rounded'>
            <p className='text-xs text-red-600'>{String(data.error)}</p>
          </div>
        )}
      </div>
    </BaseNode>
  );
}
